import { Link, Navigate, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";

import { T } from "@repo/transifex";
import {
  ScreenErrorText,
  ScreenErrorTitle,
  ScreenError
} from "~/components/screen/screen-error";
import { LoadingSpinner } from "~/components/LoadingSpinner";
import { LayoutLock } from "~/components/layout/layout-lock";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { DisplayErrorMessage } from "~/components/display-error-message";
import { useToast } from "~/components/hooks/use-toast";
import { useScanner } from "~/hooks/use-scanner";
import { CODE_FORMATS } from "~/hooks/use-scanner/code-formats";
import { useIdentifyUser } from "~/hooks/use-identify-user";
import { type UserIdentifier } from "~/providers/store/employee/store.types";
import { scanToastError } from "~/components/scanner-listener";
import { DialogError } from "~/components/DialogError";
import { Button } from "~/components/Button";
import { employeeActions } from "~/providers/store/employee/actions";
import { ScanCardSvg } from "~/components/assets/scan-card-svg";
import { useIsEmployeeAuthenticated } from "~/providers/store/employee";

export const EmployeeCardPage = () => {
  const { data: till, status: tillStatus } = useCurrentTill();

  if (
    tillStatus === "error" ||
    till?.type !== "self-service" ||
    !till.config.hasEmployeeCardReader
  ) {
    return (
      <DialogError childrenPosition="top" size="md">
        <T _str="Employee card scanning is not available" />

        <Link to="/pos">
          <Button variant="danger">
            <T _str="Back" />
          </Button>
        </Link>
      </DialogError>
    );
  }

  return <EmployeeCardScanner guestSelector={till?.config.guestSelector} />;
};

type EmployeeCardScannerProps = {
  guestSelector: string | null;
};

const EmployeeCardScanner = ({ guestSelector }: EmployeeCardScannerProps) => {
  const { dismiss } = useToast();

  const { mutate: identifyUser, status, reset, error } = useIdentifyUser();
  const isUserAuthenticated = useIsEmployeeAuthenticated();

  const handleScan = (userIdentifier: UserIdentifier) => {
    identifyUser(
      {
        userIdentifier,
        diningOption: null
      },
      {
        onError: () => {
          dismiss();
        }
      }
    );
  };

  useScanner({
    onSuccess: (result) => {
      switch (result.format) {
        case CODE_FORMATS.QR_CHILD_ID: {
          handleScan({
            type: "child-id",
            value: result.value
          });
          break;
        }

        case CODE_FORMATS.BARCODE_PRODUCT_OR_GENERIC:
        case CODE_FORMATS.GENERIC: {
          handleScan({
            type: "child-card-hex",
            value: result.value
          });
          break;
        }

        default: {
          scanToastError(null);
        }
      }
    }
  });

  if (isUserAuthenticated) {
    return <Navigate to="/pos" />;
  }

  if (status === "error") {
    return (
      <ScreenError onClose={reset} withAnimation={false}>
        <ScreenErrorTitle>
          <DisplayErrorMessage error={error} />
        </ScreenErrorTitle>

        <ScreenErrorText>
          <T _str="Please try again or ask staff for help" />
        </ScreenErrorText>
      </ScreenError>
    );
  }

  if (status === "pending") {
    return (
      <LayoutLock>
        <LoadingSpinner />
      </LayoutLock>
    );
  }

  return (
    <LayoutLock>
      <p className="text-center text-5xl font-normal">
        <T _str="Lunch registration" />
      </p>

      <ScanCardSvg />

      <p className="text-center text-5xl font-bold">
        <T _str="Scan your card" />
      </p>

      {guestSelector ? <GuestButton guestSelector={guestSelector} /> : null}
    </LayoutLock>
  );
};

type GuestButtonProps = {
  guestSelector: string;
};

const GuestButton = ({ guestSelector }: GuestButtonProps) => {
  const navigate = useNavigate();

  return (
    <Button
      onClick={() => {
        employeeActions.setAuthEmployee({ groupName: guestSelector });
        navigate("/pos");
      }}
      size="xl"
      variant="info-inverted"
    >
      <T _str="Continue as" />

      <span className="ml-1">
        <b>
          <T _str="Guest" />
          <FontAwesomeIcon className="ml-2" icon={faArrowRight} />
        </b>
      </span>
    </Button>
  );
};
